import { useState } from "react"
import { IconCheckOutline18 as Check } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"
import { Input } from "@productlane/ui"
import {
	CommandItem,
	CommandSeparator,
} from "@productlane/ui/src/components/internal/command"

import type { NumericFilter, NumericOperator } from "./types"

function formatValue(v: number, prefix?: string, suffix?: string) {
	return `${prefix ?? ""}${v}${suffix ? ` ${suffix}` : ""}`
}

const OPERATORS: {
	operator: NumericOperator
	label: (v: number, prefix?: string, suffix?: string) => string
}[] = [
	{
		operator: ">=",
		label: (v, prefix, suffix) =>
			`Greater than or equal to ${formatValue(v, prefix, suffix)}`,
	},
	{
		operator: "<=",
		label: (v, prefix, suffix) =>
			`Less than or equal to ${formatValue(v, prefix, suffix)}`,
	},
	{
		operator: "=",
		label: (v, prefix, suffix) => `Equal to ${formatValue(v, prefix, suffix)}`,
	},
	{
		operator: "!=",
		label: (v, prefix, suffix) =>
			`Not equal to ${formatValue(v, prefix, suffix)}`,
	},
]

export function NumericComparisonFilterItems({
	value,
	onChange,
	onSelect,
	placeholder = "Enter a number...",
	prefix,
	suffix,
}: {
	value: NumericFilter | null
	onChange: (filter: NumericFilter | null) => void
	onSelect: () => void
	placeholder?: string
	prefix?: string
	suffix?: string
}) {
	const [input, setInput] = useState(value?.value?.toString() ?? "")

	const numericValue = input.trim() ? Number(input.trim()) : null
	const isValid = numericValue !== null && !isNaN(numericValue)

	return (
		<>
			<div className="px-3 py-2">
				<Input
					name={placeholder}
					type="text"
					inputMode="numeric"
					autoFocus
					placeholder={placeholder}
					value={input}
					onChange={(e) => {
						const v = e.target.value
						if (v === "" || /^-?\d*\.?\d*$/.test(v)) {
							setInput(v)
						}
					}}
					variant="borderless"
				/>
			</div>
			{isValid && <CommandSeparator className="mb-2" />}
			{isValid && (
				<>
					{OPERATORS.map(({ operator, label }) => {
						const isSelected =
							value?.operator === operator && value?.value === numericValue
						return (
							<CommandItem
								key={operator}
								value={`${operator} ${numericValue}`}
								onSelect={() => {
									if (isSelected) {
										onChange(null)
									} else {
										onChange({ operator, value: numericValue })
									}
									onSelect()
								}}
							>
								<span className="flex w-full items-center justify-between">
									<span className="truncate">
										{label(numericValue, prefix, suffix)}
									</span>
									<Check
										className={cn("h-4 w-4 shrink-0", {
											"opacity-100": isSelected,
											"opacity-0": !isSelected,
										})}
									/>
								</span>
							</CommandItem>
						)
					})}
				</>
			)}
		</>
	)
}
